import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fab,
} from '@mui/material';
import { CloudUpload, Close, Upload, AttachFile } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { getLocalStorageItem } from '../../common/utils/local-storage';
import { ACCESS_TOKEN } from '../../common/constants/local-storage-keys';
import { submitAttendanceCorrection, setAttendanceCorrectionSuccess, setAttendanceCorrectionError } from '../../redux/actions/view-attendance';
import { getAllUserDetails } from '../../redux/actions';
import { getFullName } from '../../common/utils/datatable';
import { getEmployeeDropdownData } from '../../http/requests/session';
import './CorrectAttendanceForm.css';

const CorrectAttendanceForm = () => {
  const dispatch = useDispatch();
  const { regularizeAttendanceStatus, regularizeAttendanceError } = useSelector(({ MODULES }) => MODULES);
  const { allUserDetails } = useSelector(({ USER }) => USER);
  
  console.log('Component rendered - Redux state:', { regularizeAttendanceStatus, regularizeAttendanceError, allUserDetails });
  
  // Form states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState('');
  const [attendanceTime, setAttendanceTime] = useState('');
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeDropdownData, setEmployeeDropdownData] = useState([]);
  
  // Bulk upload states
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadDate, setBulkUploadDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({ success: false, error: null });

  // Load employee dropdown data on component mount
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        console.log('Fetching employee dropdown data...');
        const response = await getEmployeeDropdownData();
        console.log('Employee dropdown data response:', response);
        setEmployeeDropdownData(response);
      } catch (error) {
        console.error('Error fetching employee dropdown data:', error);
        // Fallback to getAllUserDetails if employee dropdown fails
        if (!allUserDetails || allUserDetails.length === 0) {
          dispatch(getAllUserDetails());
        }
      }
    };

    fetchEmployeeData();
  }, [dispatch, allUserDetails]);

  // Debug: Log the user data to see the structure
  useEffect(() => {
    if (allUserDetails?.data) {
      console.log('Available users for debugging search:', allUserDetails.data.slice(0, 5)); // Log first 5 users
      console.log('Total users loaded:', allUserDetails.data.length);
    }
  }, [allUserDetails]);

  // Filter employees based on search term
  const filteredEmployees = React.useMemo(() => {
    // Use employee dropdown data if available, otherwise fallback to allUserDetails
    const sourceData = employeeDropdownData.length > 0 ? employeeDropdownData : (allUserDetails?.data || []);
    
    if (!sourceData.length) return [];
    
    if (!searchTerm.trim()) {
      return sourceData;
    }
    
    const search = searchTerm.toLowerCase().trim();
    console.log('Filtering with search term:', search);
    
    const filtered = sourceData.filter((user) => {
      // Handle both employee dropdown format and allUserDetails format
      const name = user.name || getFullName(user.firstName, user.lastName);
      const email = user.email || '';
      const employeeId = user.employeeId || '';
      
      const matches = name.toLowerCase().includes(search) ||
                     email.toLowerCase().includes(search) ||
                     employeeId.toLowerCase().includes(search);
      
      return matches;
    });
    
    console.log('Search results count:', filtered.length);
    console.log('First few results:', filtered.slice(0, 3).map(u => u.name || getFullName(u.firstName, u.lastName)));
    
    return filtered;
  }, [employeeDropdownData, allUserDetails?.data, searchTerm]);

  // Handle API response - Submit attendance correction endpoint
  useEffect(() => {
    console.log('regularizeAttendanceStatus changed:', regularizeAttendanceStatus);
    if (regularizeAttendanceStatus) {
      toast.success('Attendance regularized successfully!');
      handleReset();
      setIsSubmitting(false);
    }
  }, [regularizeAttendanceStatus]);

  useEffect(() => {
    console.log('regularizeAttendanceError changed:', regularizeAttendanceError);
    if (regularizeAttendanceError) {
      // Handle different error formats
      let errorMessage = 'Error in attendance regularization';
      
      if (typeof regularizeAttendanceError === 'string') {
        errorMessage = regularizeAttendanceError;
      } else if (regularizeAttendanceError.errorMessage) {
        errorMessage = regularizeAttendanceError.errorMessage;
      } else if (regularizeAttendanceError.message) {
        errorMessage = regularizeAttendanceError.message;
      } else if (regularizeAttendanceError.data && regularizeAttendanceError.data.errorMessage) {
        errorMessage = regularizeAttendanceError.data.errorMessage;
      }
      
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  }, [regularizeAttendanceError]);

  // Cleanup Redux state when component unmounts to prevent toasts on tab switch
  useEffect(() => {
    // Clear Redux state on component mount to prevent stale toasts
    dispatch(setAttendanceCorrectionSuccess(null));
    dispatch(setAttendanceCorrectionError(null));
    
    return () => {
      // Clear the Redux state when component unmounts
      dispatch(setAttendanceCorrectionSuccess(null));
      dispatch(setAttendanceCorrectionError(null));
    };
  }, [dispatch]);

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    console.log('=== FORM SUBMISSION DEBUG START ===');
    console.log('Form submission started');
    console.log('Current regularizeAttendanceStatus:', regularizeAttendanceStatus);
    console.log('Current regularizeAttendanceError:', regularizeAttendanceError);
    
    // Validation
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    if (!attendanceDate) {
      toast.error('Please select a date');
      return;
    }
    if (!attendanceTime) {
      toast.error('Please select a time');
      return;
    }
    if (!remarks.trim()) {
      toast.error('Please enter remarks');
      return;
    }

    console.log('All validations passed');
    setIsSubmitting(true);
    
    // Create ISO date string for the selected date
    const attendanceDateISO = `${attendanceDate}T${attendanceTime}:00`;
    
    const formData = {
      EmployeeID: selectedEmployee.employeeId || selectedEmployee.azureId || selectedEmployee.id, // Send EmployeeID field (Pascal case)
      AttendanceDate: attendanceDateISO, // Include time in the date (Pascal case)
      InTime: attendanceTime, // Already in HH:mm format from input (Pascal case)
      Remarks: remarks.trim() // Pascal case
    };

    console.log('Submitting missing attendance insertion:', formData);
    console.log('EmployeeID:', formData.EmployeeID);
    console.log('dispatch function:', dispatch);
    
    // Test: Create action object manually to debug
    const action = submitAttendanceCorrection(formData);
    console.log('Generated action:', action);
    console.log('Action type:', action.type);
    console.log('Action payload:', action.payload);
    
    // Use insert-missing-attendance endpoint
    console.log('About to dispatch action...');
    dispatch(action);
    console.log('Action dispatched successfully');
    console.log('=== FORM SUBMISSION DEBUG END ===');
    
    // Add a timeout to reset submitting state if no response comes back
    setTimeout(() => {
      setIsSubmitting(false);
      console.log('Timeout: Reset submitting state after 10 seconds');
    }, 10000); // 10 seconds timeout
  };

  // Handle form reset
  const handleReset = () => {
    setSelectedEmployee(null);
    setAttendanceDate('');
    setAttendanceTime('');
    setRemarks('');
  };

  // Handle cancel
  const handleCancel = () => {
    handleReset();
  };

  // Bulk upload handlers
  const handleBulkUploadOpen = () => {
    setBulkUploadOpen(true);
  };

  const handleBulkUploadClose = () => {
    if (!isUploading) {       
      setBulkUploadOpen(false);
      setBulkUploadDate('');
      setSelectedFile(null);
      setUploadResults(null);
      setUploadStatus({ success: false, error: null });
    }
  };

  const handleDownloadSample = () => {
    // Create sample data with the specified format
    const sampleData = [
      ['UserID', 'Name', 'PunchTime', 'IOType', 'DeviceSourceDetail', 'Location', 'PunchMode', 'EventStatus', 'SpecialFunction'],
      ['20374', 'Anisha Rawat', '11/06/2025 08:55:09', 'IN', '7 : 8th Floor Front Door', '', 'Fingerprint', 'Success', '']
    ];

    // Create a new workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance Data');
    
    // Generate Excel file and download
    XLSX.writeFile(wb, 'attendance_upload_sample.xlsx');
    
    toast.success('Sample Excel file downloaded successfully');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (allowedTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
        event.target.value = '';
      }
    }
  };

  const handleBulkUploadSubmit = async () => {
    // Validation
    if (!selectedFile) {
      toast.error('Please select an Excel file');
      return;
    }

    setIsUploading(true);
    setUploadStatus({ success: false, error: null });
    setUploadResults(null);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      console.log('Bulk upload data:', {
        file: selectedFile.name,
        size: selectedFile.size
      });

      const accessToken = getLocalStorageItem(ACCESS_TOKEN);
      if (!accessToken) {
        throw new Error('No authorization token found');
      }

      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const response = await fetch(`${baseUrl}/api/BioAttendance/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        const successCount = data.successCount || 0;
        const failedCount = data.failedCount || 0;
        const failedRows = data.failedRows || [];

        setUploadResults({
          total: successCount + failedCount,
          successCount,
          failedCount,
          failedRows
        });

        if (failedCount === 0) {
          setUploadStatus({ success: true, error: null });
          toast.success(`Successfully uploaded ${successCount} attendance records`);
        } else if (successCount === 0) {
          setUploadStatus({ success: false, error: 'All records failed to upload' });
          toast.error(`Failed to upload all ${failedCount} records`);
        } else {
          setUploadStatus({ 
            success: true, 
            error: `${failedCount} records failed to upload` 
          });
          toast.warning(`Partially successful: ${successCount} uploaded, ${failedCount} failed`);
        }
      } else {
        setUploadStatus({ 
          success: false, 
          error: data.message || 'Failed to upload attendance records' 
        });
        toast.error(data.message || 'Failed to upload attendance records');
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      setUploadStatus({ 
        success: false, 
        error: 'Network error during upload' 
      });
      toast.error('Error during bulk upload: ' + (error.message || 'Network error'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div 
        className="regularize-attendance-form-wrapper"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '2rem',
          backgroundColor: '#f5f5f5'
        }}
      >
      <Paper 
        elevation={3} 
        className="regularize-attendance-form-container" 
        sx={{ 
          padding: 6, 
          width: '100%',
          maxWidth: '1000px',
          minHeight: '700px'
        }}
      >
        <div className="regularize-attendance-form-topbar"></div>
        
        {/* Header with Title and Bulk Upload Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 4 
        }}>
          <Typography variant="h4" className="regularize-attendance-form-title" sx={{ fontWeight: 'bold', color: '#1976d2', margin: 0 }}>
            Regularize Attendance
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleBulkUploadOpen}
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '12px 24px',
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: '160px',
              height: '48px',
              boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
              }
            }}
          >
            Bulk Upload
          </Button>
        </Box>
        {/* Disclaimer at the top as a red box */}
        <Box 
          className="disclaimer-box"
          sx={{
            background: '#fee2e2',
            border: '2px solid #f87171',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '32px',
            boxShadow: '0 1px 3px rgba(248, 113, 113, 0.1)'
          }}
        >
          <Typography 
            variant="body1" 
            className="disclaimer-text"
            sx={{
              color: '#dc2626',
              fontWeight: 500,
              fontSize: '1rem',
              lineHeight: 1.6,
              margin: 0
            }}
          >
            <strong>Note:</strong> Please select an employee from the dropdown and specify the date to regularize attendance.<br /><br />
            Ensure you have manager approval via email before proceeding with attendance regularization for the selected employee.
          </Typography>
        </Box>
        <Box className="regularize-attendance-form-content">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4} className="regularize-grid-container">
              {/* Employee Name - Full width on its own line */}
              <Grid item xs={12} className="employee-name-field regularize-grid-item">
                <div className="regularize-attendance-input">
                  <Autocomplete
                    fullWidth
                    options={filteredEmployees}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      // Handle both employee dropdown format and allUserDetails format
                      const name = option.name || getFullName(option.firstName, option.lastName);
                      const employeeId = option.employeeId || '';
                      // Display format: "Name (EmployeeID)" similar to session nomination
                      return employeeId ? `${name} (${employeeId})` : name;
                    }}
                    value={selectedEmployee}
                    onChange={(event, newValue) => {
                      console.log('Employee selected:', newValue);
                      setSelectedEmployee(newValue);
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      console.log('Input change - value:', newInputValue, 'reason:', reason);
                      if (reason === 'input') {
                        setSearchTerm(newInputValue);
                      } else if (reason === 'clear') {
                        setSearchTerm('');
                        setSelectedEmployee(null);
                      }
                    }}
                    className="regularize-autocomplete"
                    classes={{
                      popper: 'regularize-autocomplete-popper',
                      listbox: 'regularize-autocomplete-listbox',
                      option: 'regularize-autocomplete-option'
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee *"
                        placeholder="Type to search employees by name, ID, or email..."
                        helperText="Start typing to filter employees"
                        className="regularize-text-field"
                        sx={{ 
                          '& .MuiInputBase-root': { minHeight: '56px' },
                          '& .MuiInputLabel-root': { 
                            color: '#374151 !important',
                            fontWeight: 500,
                            fontSize: '1.5rem'
                          },
                          '& .MuiInputLabel-asterisk': { color: '#d32f2f !important' },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '1.5rem',
                            backgroundColor: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#9ca3af'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#1976d2'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#1976d2',
                              borderWidth: '2px'
                            }
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#6b7280',
                            fontSize: '0.8rem',
                            marginTop: '4px'
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} className="regularize-autocomplete-option">
                        <div style={{ width: '100%' }}>
                          <div style={{ fontWeight: 500 }}>
                            {option.name || getFullName(option.firstName, option.lastName)} 
                            {option.employeeId && (
                              <span style={{ color: '#666', fontWeight: 400 }}>
                                {' '}({option.employeeId})
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email}
                          </div>
                        </div>
                      </li>
                    )}
                    ListboxProps={{
                      sx: { maxHeight: 300 },
                      className: 'regularize-autocomplete-listbox'
                    }}
                    noOptionsText={searchTerm ? "No employees found matching your search" : "Start typing to search employees"}
                    isOptionEqualToValue={(option, value) => {
                      // Handle both data formats for comparison
                      const optionId = option?.azureId || option?.id;
                      const valueId = value?.azureId || value?.id;
                      return optionId === valueId;
                    }}
                    freeSolo={false}
                  />
                </div>
              </Grid>
              
              {/* Date and Time on same line */}
              <Grid item xs={6} className="regularize-grid-item">
                <div className="regularize-attendance-input">
                  <TextField
                    fullWidth
                    type="date"
                    label="Date *"
                    value={attendanceDate || ''}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: dayjs().format('YYYY-MM-DD'),
                    }}
                    className="regularize-text-field"
                    sx={{ 
                      '& .MuiInputBase-root': { minHeight: '56px' },
                      '& .MuiInputLabel-root': { 
                        color: '#374151 !important',
                        fontWeight: 500,
                        fontSize: '1.5rem'
                      },
                      '& .MuiInputLabel-asterisk': { color: '#d32f2f !important' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '1.5rem',
                        backgroundColor: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#9ca3af'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          borderWidth: '2px'
                        },
                        '& input[type="date"]': {
                          minWidth: '150px',
                          fontSize: '1.5rem',
                          padding: '14px'
                        }
                      }
                    }}
                  />
                </div>
              </Grid>
              <Grid item xs={6} className="regularize-grid-item">
                <div className="regularize-attendance-input">
                  <TextField
                    fullWidth
                    type="time"
                    label="Time *"
                    value={attendanceTime || ''}
                    onChange={(e) => setAttendanceTime(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    className="regularize-text-field"
                    sx={{ 
                      '& .MuiInputBase-root': { minHeight: '56px' },
                      '& .MuiInputLabel-root': { 
                        color: '#374151 !important',
                        fontWeight: 500,
                        fontSize: '1.5rem'
                      },
                      '& .MuiInputLabel-asterisk': { color: '#d32f2f !important' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '1.5rem',
                        backgroundColor: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#9ca3af'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          borderWidth: '2px'
                        },
                        '& input[type="time"]': {
                          minWidth: '150px',
                          fontSize: '1.5rem',
                          padding: '14px'
                        }
                      }
                    }}
                  />
                </div>
              </Grid>
              
              {/* Remarks - Full width on its own line */}
              <Grid item xs={12} className="regularize-grid-item">
                <div className="regularize-attendance-input">
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Remarks *"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter reason for attendance regularization..."
                    className="regularize-text-field"
                    sx={{
                      '& .MuiInputLabel-root': { 
                        color: '#374151 !important',
                        fontWeight: 500,
                        fontSize: '1.5rem'
                      },
                      '& .MuiInputLabel-asterisk': { color: '#d32f2f !important' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '1.5rem',
                        backgroundColor: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#9ca3af'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  />
                </div>
              </Grid>
              {/* Action Buttons */}
              <Grid item xs={12} className="regularize-grid-item">
                <Box 
                  className="regularize-attendance-form-actions"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 4,
                    marginTop: 5
                  }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="regularize-button regularize-button-outlined"
                    sx={{
                      minWidth: '220px',
                      height: '60px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '1.2rem',
                      textTransform: 'none',
                      borderColor: '#d1d5db',
                      color: '#374151',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f9fafb',
                        borderColor: '#9ca3af',
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    className="regularize-button regularize-button-contained"
                    sx={{
                      minWidth: '220px',
                      height: '60px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '1.2rem',
                      textTransform: 'none',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                        transform: 'none',
                        boxShadow: 'none'
                      },
                      '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#999'
                      }
                    }}
                  >
                    {isSubmitting ? 'Regularizing...' : 'Regularize Attendance'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog
        open={bulkUploadOpen}
        onClose={handleBulkUploadClose}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
            width: '400px',
            maxWidth: '90vw'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 600, 
          color: '#1976d2',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Bulk Upload Attendance
          <IconButton
            onClick={handleBulkUploadClose}
            sx={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ padding: '24px' }}>
          <Grid container spacing={3}>
            {/* File Upload */}
            <Grid item xs={12}>
              <Box sx={{ 
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#f9fafb',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f0f8ff'
                }
              }}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="bulk-upload-file"
                />
                <label htmlFor="bulk-upload-file" style={{ cursor: 'pointer' }}>
                  <CloudUpload sx={{ fontSize: '48px', color: '#1976d2', marginBottom: '16px' }} />
                  <Typography variant="h6" sx={{ marginBottom: '8px', color: '#374151', fontSize: '1.3rem' }}>
                    {selectedFile ? selectedFile.name : 'Choose Excel File'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '1.1rem' }}>
                    Click to browse or drag and drop your Excel file (.xlsx, .xls)
                  </Typography>
                </label>
              </Box>
            </Grid>



            {/* File Info */}
            {selectedFile && !uploadResults && (
              <Grid item xs={12}>
                <Box sx={{
                  backgroundColor: '#e8f5e8',
                  border: '1px solid #4caf50',
                  borderRadius: '6px',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '1.1rem' }}>
                    ✓ File selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Upload Results */}
            {uploadResults && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  {uploadResults.successCount > 0 && (
                    <Box sx={{
                      backgroundColor: '#e8f5e8',
                      border: '1px solid #4caf50',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '16px'
                    }}>
                      <Typography variant="subtitle1" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                        Successfully uploaded {uploadResults.successCount} of {uploadResults.total} records
                      </Typography>
                    </Box>
                  )}

                  {uploadResults.failedCount > 0 && (
                    <Box sx={{
                      backgroundColor: '#ffebee',
                      border: '1px solid #ef5350',
                      borderRadius: '6px',
                      padding: '12px'
                    }}>
                      <Typography variant="subtitle1" sx={{ color: '#c62828', fontWeight: 600, mb: 1 }}>
                        Failed to upload {uploadResults.failedCount} records
                      </Typography>
                      {uploadResults.failedRows && uploadResults.failedRows.length > 0 && (
                        <Box sx={{ mt: 1, maxHeight: '200px', overflowY: 'auto' }}>
                          <Typography variant="subtitle2" sx={{ color: '#c62828', mb: 1 }}>
                            Failed Records:
                          </Typography>
                          {uploadResults.failedRows.map((row, index) => (
                            <Typography key={index} variant="body2" sx={{ 
                              color: '#c62828', 
                              mb: 0.5,
                              fontSize: '0.9rem',
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap'
                            }}>
                              • Row {row.rowNumber}: {row.error}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          padding: '20px 24px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Button
            onClick={handleBulkUploadClose}
            variant="outlined"
            disabled={isUploading}
            sx={{
              borderColor: '#d32f2f',
              color: '#d32f2f',
              backgroundColor: '#ffebee',
              padding: '10px 20px',
              fontSize: '1.2rem',
              textTransform: 'none',
              borderRadius: '4px',
              minWidth: '140px',
              height: '40px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkUploadSubmit}
            variant="contained"
            disabled={isUploading || !selectedFile}
            sx={{
              backgroundColor: selectedFile ? '#1976d2' : '#ccc',
              color: 'white',
              padding: '10px 20px',
              fontSize: '1.2rem',
              textTransform: 'none',
              borderRadius: '4px',
              minWidth: '140px',
              height: '40px',
              cursor: selectedFile ? 'pointer' : 'not-allowed',
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload Attendance'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CorrectAttendanceForm;
